import { z } from 'zod';
import { normalizeString } from 'utils/Validations';
import { TipoLimiteEnum } from 'repository/timekeeper/@types/TypeLimitEnum';
import { TipoCheckLimitEnum } from 'repository/timekeeper/@types/TypeCheckLimitEnum';
import { TypeShiftLimitEnum } from 'repository/timekeeper/@types/TypeShiftLimitEnum';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';

export {  TipoCheckLimitEnum,   };

export interface LimitFormValues {
    name: string;
    description?: string;
    type_limit: TipoLimiteEnum;
    modify_by_etc: boolean;
    type_check_limit: TipoCheckLimitEnum;
    type_shift_limit: TypeShiftLimitEnum | null;
    start_month: number | null;
    start_day: number | null;
    end_month: number | null;
    end_day: number | null;
    frecuency: number | null;
    type_unit_time: FrequencyUnitTime | null;
    date_pattern_id: number | null;
}

export interface LimitPayload {
    name: string;
    description: string | null;
    type_limit: TipoLimiteEnum;
    modify_by_etc: boolean;
    type_check_limit: TipoCheckLimitEnum;
    type_shift_limit: TypeShiftLimitEnum | null;
    start_month: number | null;
    start_day: number | null;
    end_month: number | null;
    end_day: number | null;
    frecuency: number | null;
    type_unit_time: FrequencyUnitTime | null;
    date_pattern_id: number | null;
}

export const defaultLimitValues: LimitFormValues = {
    name: '',
    description: '',
    type_limit: TipoLimiteEnum.CANTIDAD_GANANCIA,
    modify_by_etc: false,
    type_check_limit: TipoCheckLimitEnum.TEMPORADA,
    type_shift_limit: TypeShiftLimitEnum.MAXIMO,
    start_month: 1,
    start_day: 1,
    end_month: 1,
    end_day: 1,
    frecuency: 0,
    type_unit_time: FrequencyUnitTime.DIAS,
    date_pattern_id: null,
};

export const createLimitSchema = (t: any, rows: any[], currentId?: number) => {
    return z.object({
        name: z.string()
            .transform(val => val.trim())
            .pipe(
                z.string()
                    .min(2, { message: t('general.zod.string.min_chars', { min: 2 }) })
                    .max(100, { message: t('general.zod.string.max_chars', { max: 100 }) })
                    .refine(
                        (val) => /^[a-zA-Z0-9\sáéíóúÁÉÍÓÚñÑüÜ\-_]+$/.test(val),
                        { message: t('general.zod.string.invalid_characters') }
                    )
                    .refine(
                        (val) => {
                            const normalizedName = normalizeString(val);
                            if (currentId !== undefined) {
                                const selected = rows.find((row: any) => row.id === currentId);
                                if (selected && normalizeString(selected.name) === normalizedName) {
                                    return true;
                                }
                            }
                            return !rows.some((row: any) => normalizeString(row.name) === normalizedName);
                        },
                        { message: t('accruals.limits.dialog.alerts.add.exist_name') }
                    )
            ),
        description: z.string().max(255, { message: t('general.zod.string.max_chars', { max: 255 }) }).optional(),
        type_limit: z.enum(TipoLimiteEnum),
        modify_by_etc: z.boolean(),
        type_check_limit: z.enum(TipoCheckLimitEnum),
        type_shift_limit: z.enum(TypeShiftLimitEnum).nullable(),
        start_month: z.number().min(1).max(12).nullable(),
        start_day: z.number().min(1).max(31).nullable(),
        end_month: z.number().min(1).max(12).nullable(),
        end_day: z.number().min(1).max(31).nullable(),
        frecuency: z.number().min(0).max(100).nullable(),
        type_unit_time: z.enum(FrequencyUnitTime).nullable(),
        date_pattern_id: z.number().nullable(),
    });
};

export const buildLimitPayload = (data: LimitFormValues): LimitPayload => {
    const isTemporada = data.type_check_limit === TipoCheckLimitEnum.TEMPORADA
        && data.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA
        && data.type_limit !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION;
    const isContinuo = data.type_check_limit === TipoCheckLimitEnum.CONTINUO
        && data.type_limit !== TipoLimiteEnum.BALANCE_GANANCIA
        && data.type_limit !== TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION;

    return {
        name: data.name,
        description: data.description || null,
        type_limit: data.type_limit,
        modify_by_etc: data.modify_by_etc,
        type_check_limit: data.type_check_limit,
        start_month: isTemporada ? data.start_month : null,
        start_day: isTemporada ? data.start_day : null,
        end_month: isTemporada ? data.end_month : null,
        end_day: isTemporada ? data.end_day : null,
        frecuency: isContinuo ? data.frecuency : null,
        type_unit_time: isContinuo ? data.type_unit_time : null,
        type_shift_limit: data.type_limit === TipoLimiteEnum.DESPLAZAMIENTO_ACUMULACION ? data.type_shift_limit : null,
        date_pattern_id: data.date_pattern_id,
    };
};
